import Big from "big.js";
import { useCallback, useEffect, useMemo } from "react";
import {
  DatabaseEnum,
  DatabaseRow,
} from "@Shape-Digital/kudzu-data/lib/types/common";
import useDataState from "../../../hooks/useDataState";
import { useSupabaseClient } from "../../../common/supabaseClient";

export type SupabaseBookingPackageOffering = Pick<
  DatabaseRow<"center_offerings">,
  | "id"
  | "offering_id"
  | "name"
  | "description"
  | "deposit_amount"
  | "price"
  | "scan_side"
  | "duration_minutes"
  | "radiology_specialty"
>;

export type SupabaseBookingPackage = Pick<
  DatabaseRow<"center_packages">,
  | "id"
  | "name"
  | "description"
  | "visibility"
  | "center_id"
  | "price"
  | "deposit_amount"
  | "marketing_text"
  | "duration_minutes"
> & {
  center_offerings: SupabaseBookingPackageOffering[];
};

const supabaseBookingPackagesRequest = `
id,
name,
description,
price,
deposit_amount,
visibility,
center_id,
marketing_text,
duration_minutes,
center_offerings (
  id,
  offering_id,
  name,
  description,
  deposit_amount,
  price,
  scan_side,
  duration_minutes,
  radiology_specialty
)
`;

export type BookingPackageOffering = {
  id: string;
  offeringId: string;
  name: string;
  price: number;
};

export type BookingPackage = {
  id: string;
  name: string;
  description: string;
  depositAmount: number;
  price: number;
  durationMinutes: number;
  discountPrice: number;
  studyIds: string[];
  globalStudyIds: string[];
  addOnIds: string[];
  marketingText?: string | null;
  offerings?: BookingPackageOffering[];
  grossPrice?: number;
  radiologySpecialties: string[];
};

export const parsePackage = (
  packageItem: SupabaseBookingPackage,
  defaultOfferingDurationMinutes: number,
  discountAmount: number,
): BookingPackage => {
  const {
    id,
    name,
    description,
    center_offerings,
    price,
    deposit_amount,
    marketing_text,
    duration_minutes,
  } = packageItem;

  const offeringsDurationMinutes = center_offerings.reduce(
    (sum, offering) =>
      sum + (offering.duration_minutes ?? defaultOfferingDurationMinutes),
    0,
  );

  const durationMinutes = duration_minutes || offeringsDurationMinutes;

  const discountPrice = discountAmount
    ? Big(price).minus(discountAmount).toNumber()
    : price;

  const studyIds = center_offerings.map((study) => study.id);
  const globalStudyIds = center_offerings
    .map((study) => study.offering_id)
    .filter(Boolean) as string[];

  const grossPrice = center_offerings.reduce(
    (sum, offering) => sum + (offering.price ?? 0),
    0,
  );

  const offerings = center_offerings.map((offering) => ({
    id: offering.id,
    offeringId: offering.offering_id ?? "",
    name: offering.name,
    price: offering.price,
  }));

  const radiologySpecialties = Array.from(
    new Set(
      center_offerings
        .map((o) => o.radiology_specialty)
        .filter((s): s is string => s !== null && s !== undefined),
    ),
  );

  return {
    id,
    name,
    description,
    depositAmount: deposit_amount,
    price,
    durationMinutes,
    discountPrice,
    studyIds,
    globalStudyIds,
    addOnIds: [],
    marketingText: marketing_text,
    offerings,
    grossPrice,
    radiologySpecialties,
  };
};

type PackagesCenterConfig = {
  id: string;
  defaultOfferingDurationMinutes: number;
  discountAmount: number;
};

const useBookingPackages = (params: {
  centerConfig: PackagesCenterConfig | null;
  isCenterUser: boolean;
}) => {
  const { centerConfig, isCenterUser } = params;

  const supabase = useSupabaseClient();

  const { dataState, updateDataState } = useDataState<BookingPackage[]>();

  const privateVisibilityProps = useMemo(() => {
    return isCenterUser ? ["private" as const] : [];
  }, [isCenterUser]);

  const visibility: DatabaseEnum["package_visibility"][] = useMemo(() => {
    return ["public", ...privateVisibilityProps];
  }, [privateVisibilityProps]);

  const allowedPackageOfferingVisibilities: DatabaseEnum["offering_visibility"][] =
    useMemo(() => {
      return ["public", "only_package", ...privateVisibilityProps];
    }, [privateVisibilityProps]);

  const getPackagesRequest = useCallback(async () => {
    try {
      if (!centerConfig || !supabase) return;

      const { id, defaultOfferingDurationMinutes, discountAmount } =
        centerConfig;

      updateDataState({ status: "loading" });

      const response = await supabase
        .from("center_packages")
        .select(supabaseBookingPackagesRequest as string)
        .in(
          "center_offerings.visibility" as string,
          allowedPackageOfferingVisibilities,
        )
        .in("visibility", visibility)
        .eq("center_id", id)
        .returns<SupabaseBookingPackage[]>();

      if (response.error) {
        throw new Error(response.error.message);
      }

      if (response.data) {
        const parsedPackages = response.data.map((packageItem) =>
          parsePackage(
            packageItem,
            defaultOfferingDurationMinutes,
            discountAmount,
          ),
        );

        updateDataState({ status: "success", data: parsedPackages });
      }
    } catch (err) {
      updateDataState({ status: "error", error: (err as Error).message });
    }
  }, [
    allowedPackageOfferingVisibilities,
    centerConfig,
    supabase,
    updateDataState,
    visibility,
  ]);

  useEffect(() => {
    getPackagesRequest();
  }, [getPackagesRequest]);

  return dataState;
};

export default useBookingPackages;
